import type { TestProject } from "@/features/tests/TestCard";
import { title } from "process";

const MOCK_TEST_PROJECTS: TestProject[] = [
  // ─────────────────────────────────────────────────────────
  // SPOTIFY
  // ─────────────────────────────────────────────────────────
  {
    id: "test-app-1",
    title: "Spotify App",
    category: "app-tests",
    subcategory: "Entertainment",
    platform: "mobile",
    appName: "Spotify",
    client: "Spotify",
    instructions:
      "Download the Spotify app. Sign in or create an account. Test search, playback, playlists, and offline/download features. IMPORTANT: Ensure you have downloaded and opened the app before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Installed Spotify from the Google Play Store or Apple App Store",
          "Opened the app and waited for full load",
          "Created an account or logged in successfully",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Launch Spotify from your home screen. How fast did it open, and did you see any crash dialogs, loading failures, or blank screens?",
        options: [
          "Instant, no errors",
          "Fast, minor warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Crashed on launch / failed",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Create an account or sign in to Spotify. Describe the exact flow and any errors, CAPTCHAs, or verification issues you encountered.",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed or error message shown",
          "Could not create or log in at all",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Use Spotify Search to find a specific artist or song. How accurately did the search results match your query?",
        options: [
          "Exact result appeared immediately",
          "Correct result appeared after a small delay",
          "Several similar (not exact) results appeared",
          "Search results were incomplete",
          "Search failed",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Open a song from the search results and start playback. How did the playback experience perform?",
        options: [
          "Started immediately",
          "Started after a short delay",
          "Required one retry",
          "Playback repeatedly buffered",
          "Playback failed",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "While a song is playing, use the playback controls to pause, resume, skip, and scrub through the track. Did each control respond correctly?",
        options: [
          "Every control responded immediately",
          "One control was slightly delayed",
          "Several controls were delayed",
          "One or more controls failed",
          "Playback controls were unusable",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Add the currently playing song to a playlist. Were you able to find the playlist action and save the song successfully?",
        options: [
          "Saved immediately",
          "Saved after a minor delay",
          "Action was difficult to find",
          "Save failed",
          "Playlist functionality did not work",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Test Spotify's download/offline behavior using content available to your account. Was the offline state clearly communicated?",
        options: [
          "Worked and status was clear",
          "Worked with minor confusion",
          "Status took time to update",
          "Download failed",
          "Offline state was unclear",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Rate Spotify's overall reliability and user experience during this test from 1 to 5.",
      },
      {
        id: "q9",
        type: "text",
        text: "What was the most important Spotify issue, confusing interaction, performance problem, or improvement you noticed? Include the feature and what you expected to happen.",
      },
    ],
    reward: 200,
    deadline: "2026-08-20",
    owner: "Spotify QA",
    status: "open",
    demand: "high",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },

  // ─────────────────────────────────────────────────────────
  // YOUTUBE
  // ─────────────────────────────────────────────────────────
  {
    id: "test-app-2",
    title: "YouTube App",
    category: "app-tests",
    subcategory: "Social Media",
    platform: "mobile",
    appName: "YouTube",
    client: "Google",
    instructions:
      "Download the YouTube app. Sign in with a Google account. Test video search, playback, quality settings, comments, and subscriptions. IMPORTANT: Ensure you have downloaded and opened the app before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Installed YouTube from the Google Play Store or Apple App Store",
          "Opened the app and waited for full load",
          "Signed in with a Google account",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Launch YouTube from your home screen. How fast did it open, and did you see any crash dialogs, loading failures, or blank screens?",
        options: [
          "Instant, no errors",
          "Fast, minor warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Crashed on launch / failed",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Sign in with a Google account. Describe the exact flow and any errors, CAPTCHAs, 2FA prompts, or verification issues you encountered.",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed or error message shown",
          "Could not sign in at all",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Use the search bar to find a specific video and select it from the results. Did the correct video load and start playing?",
        options: [
          "Search worked immediately and video loaded",
          "Search worked with a short delay",
          "Results were not fully relevant",
          "Video failed to load after selection",
          "Search returned no usable results",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "While watching the selected video, open the quality settings and change the playback quality. Did the new quality take effect correctly?",
        options: [
          "Changed immediately",
          "Changed after a short delay",
          "Required reopening the settings",
          "Quality option failed",
          "Settings were unavailable",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Open the comments section under the video. Were you able to read existing comments and post a new comment?",
        options: [
          "Comments loaded immediately and posted successfully",
          "Loaded with a short delay but worked",
          "Comments loaded partially",
          "Could not post a new comment",
          "Comments section failed to open",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Like the video and then save it to a playlist. Did both actions complete reliably?",
        options: [
          "Both actions completed immediately",
          "One action had a minor delay",
          "Actions were inconsistent",
          "One or both actions failed",
          "Like and save buttons were unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Open your Subscriptions or History tab. Did the page load correctly and show the expected content?",
        options: [
          "Loaded immediately with correct content",
          "Loaded after a short delay",
          "Content was partially missing",
          "Page showed a loading error",
          "Subscriptions or History was unavailable",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Rate YouTube's overall reliability and user experience during this test from 1 to 5.",
      },
      {
        id: "q9",
        type: "text",
        text: "What was the most important YouTube issue, confusing interaction, performance problem, or improvement you noticed? Include the feature and what you expected to happen.",
      },
    ],
    reward: 200,
    deadline: "2026-08-22",
    owner: "YouTube QA",
    status: "open",
    demand: "high",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },

  // ─────────────────────────────────────────────────────────
  // GOOGLE CHROME
  // ─────────────────────────────────────────────────────────
  {
    id: "test-app-3",
    title: "Google Chrome Browser",
    category: "app-tests",
    subcategory: "Technology",
    platform: "mobile",
    appName: "Google Chrome",
    client: "Google",
    instructions:
      "Download Google Chrome from your browser or the official Google Chrome website. Install and open it. Test tab management, bookmarks, incognito mode, and address bar behavior. IMPORTANT: Ensure you have downloaded and opened the app before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Installed Chrome from the Google Play Store or Apple App Store",
          "Opened the app and waited for full load",
          "Signed in to sync (or confirmed guest browsing)",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Launch Chrome from your home screen. How fast did it open, and did you see any crash dialogs, loading failures, or blank screens?",
        options: [
          "Instant, no errors",
          "Fast, minor warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Crashed on launch / failed",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Sign in to Chrome to sync bookmarks and history. Describe the sign-in flow and any issues you encountered.",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed or error message shown",
          "Could not sign in at all",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Open two different websites in separate Chrome tabs and switch between them several times. Did Chrome preserve each page's state correctly?",
        options: [
          "Both pages remained exactly as expected",
          "One page briefly reloaded",
          "Both pages reloaded",
          "One tab became unresponsive",
          "Tabs could not be switched reliably",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Add a bookmark for the current page and then find it in the Bookmarks menu. Did the bookmark save and load correctly?",
        options: [
          "Saved and loaded immediately",
          "Saved but required a refresh to appear",
          "Bookmark appeared in the wrong folder",
          "Bookmark did not save",
          "Bookmarks menu was inaccessible",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Open a new Incognito tab and visit a website. Did Incognito mode start correctly and was browsing history not saved?",
        options: [
          "Started correctly and no history was saved",
          "Started correctly but history behavior was unclear",
          "Incognito tab showed unexpected behavior",
          "Incognito mode failed to start",
          "History was saved despite Incognito",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Type a URL in the address bar and use the autocomplete suggestions. Did suggestions appear correctly and did navigation work as expected?",
        options: [
          "Suggestions appeared immediately and navigation worked",
          "Suggestions appeared with a delay",
          "Suggestions were inaccurate",
          "Navigation failed after selection",
          "Address bar was unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Use the back and forward buttons after navigating between several pages. Did Chrome return to each previous page correctly?",
        options: [
          "Navigation worked perfectly",
          "One navigation step failed",
          "Pages reloaded unexpectedly",
          "Back/forward buttons were delayed",
          "Navigation history was lost",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Rate Chrome's overall reliability and user experience during this test from 1 to 5.",
      },
      {
        id: "q9",
        type: "text",
        text: "What was the most important Chrome issue, confusing interaction, performance problem, or improvement you noticed? Include the feature and what you expected to happen.",
      },
    ],
    reward: 200,
    deadline: "2026-08-25",
    owner: "Chrome QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },

  //Duolingo App
  {
    id: "test-app-5",
    title: "Duolingo App",
    category: "app-tests",
    subcategory: "Education",
    platform: "mobile",
    appName: "Duolingo",
    client: "Duolingo",
    instructions:
      "Install the official Duolingo app from your device's app store and open it. Sign in or create an account, select a language course, complete a lesson, test the answer checking, hearts/energy system, lesson progress, and review features. IMPORTANT: You must have the app installed and opened before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm that you have completed all of the following:",
        checklist: [
          "Installed the official Duolingo app",
          "Opened Duolingo and waited for the home screen to finish loading",
          "Signed in or created a Duolingo account",
          "Selected a language course such as Spanish, French, German, or another available course",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Launch Duolingo from your device. Did the Duolingo home screen load completely, including your current course, lesson progress, and available lesson button?",
        options: [
          "Loaded immediately with all course information visible",
          "Loaded normally with a short delay",
          "Loaded but some course information appeared late",
          "Home screen was partially blank or required a retry",
          "App crashed, froze, or failed to open",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Open your selected language course and start the next available lesson. Were the lesson instructions, question prompt, and answer controls displayed correctly?",
        options: [
          "Lesson opened correctly and all controls worked",
          "Lesson opened with a minor loading delay",
          "Lesson opened but one element loaded incorrectly",
          "Lesson required a retry before it could start",
          "Lesson could not be opened",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Complete several lesson questions, including at least one multiple-choice or translation question. When you submit an answer, did Duolingo correctly indicate whether the answer was correct or incorrect?",
        options: [
          "Every answer received immediate and correct feedback",
          "Feedback was correct but occasionally delayed",
          "Some feedback was delayed or visually inconsistent",
          "At least one answer submission did not provide clear feedback",
          "Answer submission failed or the lesson became unusable",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Submit one intentionally incorrect answer during the lesson. Did Duolingo clearly show that the answer was incorrect and provide the expected/correct answer or correction?",
        options: [
          "Incorrect answer was identified immediately with the correct answer shown",
          "Correction appeared after a short delay",
          "Incorrect answer was identified but the correction was unclear",
          "The app behaved inconsistently after the incorrect answer",
          "The app did not register the incorrect answer correctly",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Complete enough questions to advance your lesson progress. Did the lesson progress indicator update correctly as you completed questions?",
        options: [
          "Progress updated after every question",
          "Progress updated with a minor visual delay",
          "Progress occasionally displayed the wrong position before updating",
          "Progress stopped updating until the lesson was refreshed",
          "Progress did not update at all",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Finish the lesson and view the results screen. Did Duolingo display your lesson completion, XP earned, and other available lesson results correctly?",
        options: [
          "Completion screen appeared immediately with correct results",
          "Results appeared after a short delay",
          "Results appeared but one value was temporarily missing",
          "Lesson completed but the results screen required a retry",
          "The lesson could not be completed or results did not appear",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Return to the Duolingo home screen after completing the lesson. Did the completed lesson and your updated progress appear correctly on the course path?",
        options: [
          "Updated immediately and matched the completed lesson",
          "Updated after a short synchronization delay",
          "Progress appeared temporarily outdated",
          "Progress only updated after reopening the app",
          "Completed lesson/progress was not recorded",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Considering lesson loading, answer checking, incorrect-answer feedback, progress tracking, and lesson completion, rate the overall reliability of Duolingo from 1 to 5.",
      },
      {
        id: "q9",
        type: "text",
        text: "Describe one specific Duolingo feature that worked particularly well or one specific problem you encountered. Include the screen or lesson step where it happened.",
      },
    ],
    reward: 200,
    deadline: "2026-08-30",
    owner: "Duolingo QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },
  //vs code
  {
    id: "test-app-6",
    title: "VS Code",
    category: "app-tests",
    subcategory: "Technology",
    platform: "web",
    appName: "VS Code",
    client: "Microsoft",
    instructions:
      "Open the official VS Code web experience in a supported browser. Create or open a small code project, edit a source file, verify syntax highlighting, use the integrated file explorer, open the command palette, and test available source-control functionality if a Git repository is available. IMPORTANT: This test evaluates the VS Code editor experience in the browser and requires access to a code project.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm that you have completed all of the following:",
        checklist: [
          "Opened the official VS Code web experience in a supported browser",
          "Waited for the editor interface to finish loading",
          "Opened an existing code project or created a small test project",
          "Opened at least one source-code file such as .js, .ts, .py, .html, or .css",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open VS Code and load your code project. Did the Explorer sidebar display the project's folders and files correctly without missing or duplicated entries?",
        options: [
          "Project loaded immediately and all expected files were visible",
          "Project loaded normally with a short delay",
          "Project loaded but some files appeared after a delay",
          "Some folders/files were missing until the project was refreshed",
          "Project failed to load or the editor became unusable",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Open a JavaScript, TypeScript, Python, HTML, or CSS file and add a small code change. Did VS Code provide the expected syntax highlighting and preserve the text while you edited the file?",
        options: [
          "Syntax highlighting and editing worked correctly throughout",
          "Editing worked with a minor rendering delay",
          "Some syntax highlighting appeared incorrectly or late",
          "The editor intermittently stopped accepting input",
          "The file could not be edited correctly",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Make a deliberate syntax mistake in the source file, such as removing a closing bracket or quote. Did VS Code identify the problem with an error marker or diagnostic?",
        options: [
          "The syntax problem was highlighted immediately",
          "The diagnostic appeared after a short delay",
          "The problem was detected but the marker was unclear",
          "The diagnostic appeared only after reopening or changing the file",
          "No diagnostic or error indication appeared",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Open the Command Palette using the available keyboard shortcut or the VS Code menu. Were commands searchable and selectable from the Command Palette?",
        options: [
          "Command Palette opened immediately and commands were searchable",
          "It opened with a short delay",
          "It opened but search results loaded slowly",
          "The Command Palette opened inconsistently",
          "The Command Palette could not be opened",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Use the file Explorer to create a new file inside the project, give it an appropriate extension such as test.js, and open it. Did the new file appear in the Explorer and open in the editor?",
        options: [
          "File was created, displayed, and opened correctly",
          "File creation worked after a short delay",
          "File appeared but required refreshing the Explorer",
          "File creation behaved inconsistently",
          "The new file could not be created or opened",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "If your project is connected to Git, open the Source Control panel and inspect the modified files. Did VS Code correctly identify the file you changed as a Git modification?",
        options: [
          "Modified file appeared immediately with the correct change status",
          "Git status appeared after a short delay",
          "The file appeared but the status was temporarily inconsistent",
          "Source Control required a refresh before showing the modification",
          "Source Control did not detect the modified file",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Save your edited source file, close it, and reopen it from the Explorer. Did your saved code remain intact after reopening the file?",
        options: [
          "Saved code was preserved exactly",
          "Saved code was preserved after a short delay",
          "Most changes were preserved but one change was missing",
          "The file required synchronization or refresh before the changes appeared",
          "Saved changes were lost or the file could not be reopened",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Considering project loading, file navigation, code editing, syntax diagnostics, Command Palette, saving, and Git/source-control behavior, rate the overall reliability of the VS Code experience from 1 to 5.",
      },
      {
        id: "q9",
        type: "text",
        text: "Describe one specific editor, project navigation, syntax diagnostic, saving, or Git issue you experienced. Include the file type and the action that caused the issue.",
      },
    ],
    reward: 200,
    deadline: "2026-09-02",
    owner: "GitHub QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must open website", "Must have code project"],
    language: "en",
  },

  // Facebook Web App
  {
    id: "test-web-1",
    title: "Facebook Web App",
    category: "web-tests",
    subcategory: "Social Media",
    platform: "web",
    appName: "Facebook",
    client: "Meta",
    instructions:
      "Open https://www.facebook.com/ in a desktop or mobile browser and sign in to an existing Facebook account. Test the News Feed, post composer, reactions, comments, notifications, and Messenger entry point. IMPORTANT: You must open the website and complete the login process before starting the feature tests.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm that you have completed all of the following:",
        checklist: [
          "Opened https://www.facebook.com/ in your browser",
          "Waited for the Facebook home page to finish loading",
          "Signed in successfully to an existing Facebook account",
          "Reached the Facebook News Feed",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "After signing in, observe the Facebook News Feed. Did the feed load your posts, suggested content, images, and other feed elements without blank sections or repeated loading failures?",
        options: [
          "Feed loaded completely with content and images",
          "Feed loaded normally with a short delay",
          "Some posts or images loaded after scrolling",
          "Several feed sections remained blank or required refresh",
          "Feed failed to load or the page became unusable",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Use the Facebook post composer to enter a short text message without publishing it. Did the composer open and allow you to enter text normally?",
        options: [
          "Composer opened immediately and accepted text normally",
          "Composer opened after a short delay",
          "Text input worked but the composer had a minor display issue",
          "Composer required reopening before accepting text",
          "Composer could not be opened or used",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Publish a simple text post using the Facebook post composer. After publishing, did the new post appear in your profile/feed with the text you entered?",
        options: [
          "Post published immediately and displayed correctly",
          "Post published after a short delay",
          "Post published but initially appeared without some information",
          "Publishing required a retry",
          "Post could not be published",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "On an existing post, select a reaction such as Like, Love, Haha, Wow, Sad, or Angry. Did the selected reaction appear on the post and remain selected after the page updated?",
        options: [
          "Reaction changed immediately and remained selected",
          "Reaction appeared after a short delay",
          "Reaction changed visually but briefly reverted",
          "Reaction required clicking again to register",
          "Reaction control did not work",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Open the comments section of an existing post and submit a short comment. Did Facebook display the comment under the correct post after submission?",
        options: [
          "Comment appeared immediately under the correct post",
          "Comment appeared after a short delay",
          "Comment posted but required refreshing to appear",
          "Comment submission was inconsistent or duplicated",
          "Comment could not be submitted",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Scroll through several feed posts and open at least one image or video. Did the selected media open correctly without causing the feed to lose its position or become unresponsive?",
        options: [
          "Media opened correctly and the feed remained usable",
          "Media opened with a short loading delay",
          "Media took several seconds to load but eventually worked",
          "Media caused the feed to jump, freeze, or require refresh",
          "Media could not be opened",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Open the Messenger entry point from Facebook and attempt to view your recent conversations. Did Messenger load the conversation list and allow you to open a conversation?",
        options: [
          "Messenger opened and conversations loaded correctly",
          "Messenger opened after a short delay",
          "Conversation list loaded slowly or partially",
          "Messenger required a refresh before conversations appeared",
          "Messenger could not be opened or became unresponsive",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Considering News Feed loading, post creation, reactions, comments, media loading, and Messenger access, rate the overall reliability of Facebook Web from 1 to 5.",
      },
      {
        id: "q9",
        type: "text",
        text: "Describe one specific Facebook Web issue you experienced. Include the feature involved, such as Feed, post composer, reaction, comment, media viewer, or Messenger, and explain what happened.",
      },
    ],
    reward: 100,
    deadline: "2026-08-24",
    owner: "Facebook QA",
    status: "open",
    demand: "high",
    estimatedTime: "3 mins",
    requirements: ["Must open website", "Must have account"],
    language: "en",
  },

  // Google Search Console Web App
  {
    id: "test-web-2",
    title: "Google Search Console Web App",
    category: "web-tests",
    subcategory: "Technology",
    platform: "web",
    appName: "Google Search Console",
    client: "Google",
    instructions:
      "Open https://search.google.com/search-console/ in your browser and sign in with a Google account that has access to at least one Search Console property. Test the property selector, Overview report, URL Inspection, Performance report, Page indexing report, and Sitemaps section. IMPORTANT: You must have access to an existing Search Console property to complete the reporting and inspection tasks.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm that you have completed all of the following:",
        checklist: [
          "Opened https://search.google.com/search-console/ in your browser",
          "Signed in with a Google account that has Search Console access",
          "Selected an existing verified Search Console property",
          "Reached the Search Console Overview page",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open the selected Search Console property and view the Overview page. Did the Overview dashboard load the property's Performance, Page indexing, and Experience information?",
        options: [
          "Dashboard loaded with all available report cards",
          "Dashboard loaded after a short delay",
          "Most cards loaded but one or more reports took longer",
          "Several report cards remained blank or required refresh",
          "Property dashboard failed to load",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Open the URL Inspection tool and enter a URL belonging to the selected property. Did Search Console accept the URL and display the inspection status?",
        options: [
          "URL inspection completed and status information appeared",
          "Inspection completed after a short delay",
          "Inspection required another attempt before returning a result",
          "Inspection returned an unclear or incomplete result",
          "URL Inspection could not be started or completed",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "In URL Inspection, review the indexing information for the URL. Were the indexing status and related details clearly presented?",
        options: [
          "Indexing status and details were clearly displayed",
          "Details appeared after a short loading delay",
          "Some details were available but one section was incomplete",
          "Information was difficult to interpret or required refresh",
          "Indexing information failed to load",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Open the Performance report and set a date range that contains available data. Did the report display metrics such as total clicks, impressions, average CTR, and average position?",
        options: [
          "All available metrics and charts loaded correctly",
          "Metrics loaded after a short delay",
          "Some metrics appeared while one or more charts loaded slowly",
          "Report required refreshing or changing the date range",
          "Performance report failed to load data",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Within the Performance report, switch between dimensions such as Queries, Pages, Countries, or Devices. Did the table update to the selected dimension?",
        options: [
          "Each selected dimension loaded the correct table",
          "Tables changed after a short delay",
          "Some dimensions loaded slowly or incompletely",
          "Dimension switching required a refresh",
          "The dimension controls did not work",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Open the Page indexing report and inspect the indexed/not-indexed summary. Did Search Console display the indexing summary and available details for the property?",
        options: [
          "Indexing summary and details loaded correctly",
          "Summary loaded after a short delay",
          "Summary loaded but some details took longer",
          "Report required a refresh before displaying information",
          "Page indexing report failed to load",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Open the Sitemaps section for the selected property. Did the page display previously submitted sitemaps and provide the interface for submitting a sitemap URL?",
        options: [
          "Existing sitemaps and submission interface loaded correctly",
          "Sitemaps loaded after a short delay",
          "Existing sitemap information was incomplete until refreshed",
          "Sitemaps page loaded but submission controls were unavailable",
          "Sitemaps section failed to load",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Considering property selection, Overview, URL Inspection, Performance, Page indexing, and Sitemaps, rate the overall reliability and usability of Google Search Console from 1 to 5.",
      },
      {
        id: "q9",
        type: "text",
        text: "Describe one specific Search Console issue you experienced. Mention the exact section, such as URL Inspection, Performance, Page indexing, Sitemaps, or Overview, and explain what happened.",
      },
    ],
    reward: 100,
    deadline: "2026-08-24",
    owner: "Google Search QA",
    status: "open",
    demand: "high",
    estimatedTime: "3 mins",
    requirements: [
      "Must open website",
      "Must have Google account",
      "Must have Search Console property access",
    ],
    language: "en",
  },

  // ============================================================
  // WEB APP TESTS
  // ============================================================

  //Canva Web App
  {
    id: "test-web-3",
    title: "Canva Web App",
    category: "web-tests",
    subcategory: "Productivity",
    platform: "web",
    appName: "Canva",
    client: "Canva",
    instructions:
      "Open https://www.canva.com/ in your browser and sign in or create a Canva account. Create a simple design using a presentation, social media, or poster template. Test the Canva editor, templates, text editing, element positioning, uploads, and design export. IMPORTANT: You must open the website and reach the Canva editor before starting the feature tests.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Opened https://www.canva.com/ and waited for the website to finish loading",
          "Created a Canva account or logged in successfully",
          "Reached the Canva home/dashboard page",
          "Opened the Canva design editor with a new or existing design",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open Canva in your browser and wait for the home/dashboard page to finish loading. Did the dashboard display your available designs, templates, and navigation controls correctly?",
        options: [
          "Dashboard loaded immediately with all main sections visible",
          "Dashboard loaded normally after a short delay",
          "Dashboard loaded but some thumbnails or sections appeared later",
          "Several sections remained blank or required a page refresh",
          "Canva failed to load or displayed a blank/error page",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Create a new Canva design and select a template category such as Presentation, Instagram Post, Poster, or Flyer. Did the template selection page load and allow you to choose a design?",
        options: [
          "Template categories and previews loaded correctly and selection worked",
          "Templates loaded after a short delay",
          "Some template previews were slow or missing",
          "Template selection required refreshing or reopening the page",
          "Templates could not be loaded or selected",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "In the Canva editor, select a text element and replace its text with a short sentence. Did the text update in the design and remain visible after clicking outside the text box?",
        options: [
          "Text changed immediately and remained correctly positioned",
          "Text changed after a short delay",
          "Text changed but the editor briefly displayed the old text",
          "Text editing required selecting the element more than once",
          "Text could not be edited or the editor became unresponsive",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Use the Elements panel to search for a specific element such as a circle, arrow, or phone icon. Add one result to the design. Did the search results and selected element appear correctly?",
        options: [
          "Search results appeared quickly and the element was added correctly",
          "Results loaded after a short delay but the element worked",
          "Some results or previews loaded slowly",
          "The element appeared only after refreshing or retrying",
          "Search or adding the element failed",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Select an element you added and drag it to another position on the canvas. Then resize it using its resize handles. Did the element move and resize accurately without unexpectedly jumping?",
        options: [
          "Element moved and resized smoothly and accurately",
          "Movement was mostly smooth with minor lag",
          "There was noticeable dragging or resizing lag",
          "The element occasionally jumped or changed position unexpectedly",
          "The element could not be moved or resized",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Upload a small image using Canva's Uploads feature and place it onto the design. Did the upload finish and allow the image to be inserted into the canvas?",
        options: [
          "Image uploaded quickly and was inserted correctly",
          "Upload completed after a short delay",
          "Upload was slow but eventually completed",
          "Upload required retrying before the image appeared",
          "Image upload failed or the image could not be inserted",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Export the completed design using Canva's Download option and select an available format such as PNG or PDF. Did Canva generate the file and start the download successfully?",
        options: [
          "File generated and downloaded successfully",
          "Download started after a short delay",
          "Export took noticeably longer than expected but completed",
          "Export required retrying or changing the format",
          "Export failed and no usable file was generated",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on template loading, the Canva editor, text editing, elements, drag-and-drop, image uploads, and design export, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "Describe one specific Canva issue or successful feature you noticed. Mention the exact area involved, such as Templates, Elements, Text, Uploads, the editor canvas, or Download, and explain what happened.",
      },
    ],
    reward: 100,
    deadline: "2026-09-01",
    owner: "Canva QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must open website", "Must have account"],
    language: "en",
  },

  // ============================================================
  // MOBILE APP TESTS
  // ============================================================

  //WhatsApp App
  {
    id: "test-app-7",
    title: "WhatsApp App",
    category: "app-tests",
    subcategory: "Social Media",
    platform: "mobile",
    appName: "WhatsApp",
    client: "Meta",
    instructions:
      "Install the official WhatsApp app on your mobile device and open it. Verify or sign in to your WhatsApp account, open an existing conversation, send a text message, share an image, test a voice note, make a voice or video call, view or update Status, and use a group conversation. IMPORTANT: Ensure WhatsApp is installed, opened, and your account is active before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Installed the official WhatsApp app on your mobile device",
          "Opened WhatsApp and waited for the Chats screen to finish loading",
          "Verified your phone number or signed in to an existing WhatsApp account",
          "Have at least one existing WhatsApp conversation or contact available for testing",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Launch WhatsApp from your device's home screen. Did the Chats screen load correctly with your recent conversations, search control, and new-chat button visible?",
        options: [
          "Chats screen opened immediately and all main controls were visible",
          "Chats screen opened normally after a short delay",
          "Chats loaded but some conversation names or previews appeared later",
          "Chats screen was partially blank or required reopening the app",
          "WhatsApp crashed, froze, or failed to open",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Open an existing WhatsApp conversation and send a short text message such as 'Test message'. Did the message appear in the conversation with the correct sent/delivered status?",
        options: [
          "Message appeared immediately with the correct delivery status",
          "Message was sent after a short delay",
          "Message appeared but delivery status updated later",
          "Message required tapping Send again or reconnecting",
          "Message could not be sent",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "From the same WhatsApp conversation, attach and send one image from your device gallery. Did the image preview, upload, and send successfully?",
        options: [
          "Image previewed, uploaded, and appeared in the chat correctly",
          "Image sent after a short upload delay",
          "Image upload was noticeably slow but completed",
          "Image required retrying before it was sent",
          "Image could not be uploaded or sent",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Record a short WhatsApp voice message and send it in the conversation. Did the voice message appear as an audio message and play correctly after sending?",
        options: [
          "Voice message recorded, sent, and played correctly",
          "Recording or playback had a minor delay",
          "Voice message sent but playback started slowly",
          "Recording or sending required retrying",
          "Voice message could not be recorded, sent, or played",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Start a WhatsApp voice call with an available contact. Did the call connect and provide two-way audio without immediately disconnecting?",
        options: [
          "Call connected quickly with clear two-way audio",
          "Call connected after a short delay",
          "Call connected but audio had noticeable delay or interruptions",
          "Call disconnected or required another attempt",
          "Call could not be started or connected",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Open the Updates/Status section and view an existing Status or create a short text/photo Status if your account allows it. Did the Status load or publish correctly?",
        options: [
          "Status loaded or published immediately and displayed correctly",
          "Status loaded or published after a short delay",
          "Status worked but media or text appeared late",
          "Status required refreshing or retrying",
          "Status could not be viewed or published",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Open an existing WhatsApp group conversation and send a short message. Did the group chat display the message under your account without freezing or incorrectly showing it as a private message?",
        options: [
          "Message appeared correctly in the group immediately",
          "Message appeared after a short delay",
          "Message appeared but the group view updated slowly",
          "Group message required retrying or reopening the conversation",
          "Group chat could not be opened or the message could not be sent",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on message sending, image sharing, voice messages, voice calls, Status, and group chats, rate the overall reliability and user experience of WhatsApp from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "Describe one specific WhatsApp issue or successful feature you noticed. Mention the exact feature involved, such as text messaging, image sharing, voice messages, calls, Status, or group chats, and explain what happened.",
      },
    ],
    reward: 100,
    deadline: "2026-09-01",
    owner: "WhatsApp QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },
  // ============================================================
  // WEB APP TESTS
  // ============================================================

  //Shopify Web App
  {
    id: "test-web-4",
    title: "Shopify Web App",
    category: "web-tests",
    subcategory: "E-commerce",
    platform: "web",
    appName: "Shopify",
    client: "Shopify",
    instructions:
      "Open a Shopify-powered storefront in your browser and test the customer shopping experience. Browse product collections, open a product page, use product search, add a product to the cart, update the cart quantity, proceed through checkout, and review the order information. IMPORTANT: This test evaluates the storefront/customer experience, not the Shopify merchant administration dashboard. Do not place a real order or enter real payment information unless the test environment specifically instructs you to do so.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Opened the assigned Shopify-powered storefront in your browser",
          "Waited for the storefront home page to finish loading",
          "Opened at least one product collection or category",
          "Reached a product details page",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open the assigned Shopify storefront and wait for the home page to finish loading. Did the store display its navigation, product sections, images, and main shopping controls correctly?",
        options: [
          "Everything loaded immediately and displayed correctly",
          "The page loaded normally with a short delay",
          "Some product images or sections loaded slowly",
          "Several sections were blank or required refreshing",
          "The storefront failed to load or displayed an unusable page",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Open a product collection/category and select a product. Did the collection display product names, prices, images, and available shopping controls correctly?",
        options: [
          "Products and all expected details displayed correctly",
          "Products loaded after a short delay",
          "Some images or product details loaded slowly",
          "Some products or prices were missing until refresh",
          "Collection or product navigation failed",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Open a product details page and inspect the product title, price, images, description, variants, and Add to Cart control. Did the product information match what was displayed in the collection?",
        options: [
          "All product information was present and consistent",
          "Information loaded after a short delay",
          "One section or image loaded slowly",
          "Some product information was missing or inconsistent",
          "Product page failed to load correctly",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Use the storefront search field to search for a specific product name or keyword. Did Shopify return relevant products and allow you to open one of the results?",
        options: [
          "Search returned relevant results immediately",
          "Results appeared after a short delay",
          "Results were relevant but loaded slowly",
          "Search returned incomplete or unexpected results",
          "Search failed or returned no usable results",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Select an available product variant such as size, color, or model and add the product to the cart. Did the selected variant and correct quantity appear in the cart?",
        options: [
          "Correct variant was added immediately",
          "Variant selection and cart update had a short delay",
          "Product was added but the cart took time to show the selected variant",
          "Cart showed an incorrect or incomplete variant",
          "Product could not be added to the cart",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Open the cart, change the quantity of the product, and continue toward checkout. Did the cart update the quantity and subtotal correctly?",
        options: [
          "Quantity and subtotal updated immediately and correctly",
          "Cart updated after a short delay",
          "Subtotal took time to recalculate",
          "Quantity or subtotal displayed an incorrect value",
          "Cart could not be updated or checkout could not be opened",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Proceed to the Shopify checkout without completing a real purchase. Review the checkout page for contact, shipping, delivery, and order-summary information. Did the checkout display the expected information and controls?",
        options: [
          "Checkout loaded correctly with all expected information",
          "Checkout loaded after a short delay",
          "Some checkout information appeared slowly",
          "A checkout section was missing or required refreshing",
          "Checkout failed to load or could not proceed",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on storefront loading, product browsing, search, product details, variants, cart updates, and checkout, rate the overall reliability and shopping experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "Describe one specific Shopify storefront issue or successful feature you noticed. Mention the exact area, such as search, product page, variant selection, cart, or checkout, and explain what happened.",
      },
    ],
    reward: 100,
    deadline: "2026-09-05",
    owner: "Shopify QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must open website", "Must have account"],
    language: "en",
  },

  // ============================================================
  // MOBILE APP TESTS
  // ============================================================

  //WhatsApp Business App
  {
    id: "test-app-8",
    title: "WhatsApp Business App",
    category: "app-tests",
    subcategory: "Entertainment",
    platform: "mobile",
    appName: "WhatsApp Business",
    client: "Meta",
    instructions:
      "Install the official WhatsApp Business app and complete the initial business profile setup. Test business profile information, greeting and away messages, quick replies, product catalog, labels, customer conversations, and sharing catalog items in chats. IMPORTANT: Ensure the WhatsApp Business app is installed, opened, and configured with a business account before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Installed the official WhatsApp Business app on your device",
          "Opened the app and waited for the Chats screen to finish loading",
          "Verified the business phone number or signed in to the WhatsApp Business account",
          "Completed enough business profile setup to access Business Tools",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Launch WhatsApp Business from your device and open the Chats screen. Did the application load your conversations and display the business messaging interface correctly?",
        options: [
          "Opened immediately with chats and controls displayed correctly",
          "Opened normally after a short delay",
          "Chats or business controls appeared slowly",
          "The screen was partially blank or required reopening",
          "App crashed, froze, or failed to launch",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Open the Business Profile settings and review the business name, category, description, business hours, and contact information. Did the profile information save and display correctly?",
        options: [
          "All available business information saved and displayed correctly",
          "Information saved after a short delay",
          "One section took longer to update",
          "Some information did not save until retrying",
          "Business profile could not be updated",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Open Business Tools and configure a Greeting Message or Away Message using a short test message. Did WhatsApp Business save the message and show that the automation was enabled?",
        options: [
          "Message saved and automation was enabled immediately",
          "Saved after a short delay",
          "Message saved but the enabled status appeared late",
          "Configuration required retrying",
          "Automation settings could not be saved",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Create a Quick Reply with a shortcut such as /hours and enter a short business-hours response. Did WhatsApp Business save the shortcut and make it available when composing a message?",
        options: [
          "Quick Reply saved and appeared correctly when requested",
          "Saved after a short delay",
          "Shortcut appeared after reopening the chat",
          "Quick Reply required editing or saving again",
          "Quick Reply could not be created or used",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Open the Catalog section and add a test product with a product name, price, description, and image. Did the product save and appear in the business catalog?",
        options: [
          "Product saved and appeared with all entered information",
          "Product saved after a short delay",
          "Product appeared but one image or detail loaded late",
          "Product required retrying before appearing",
          "Product could not be added to the catalog",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Open a customer conversation and use the chat tools to send a Quick Reply or share a catalog item. Did the selected business tool insert and send the intended content correctly?",
        options: [
          "Content inserted and sent correctly",
          "Content appeared after a short delay",
          "Content sent but required an additional tap",
          "The wrong content appeared or the action behaved inconsistently",
          "The business tool could not be used in the conversation",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Apply a customer label to a conversation, such as a test label like 'New Customer', and then view the labeled conversations. Did the label save and remain associated with the correct chat?",
        options: [
          "Label was applied and remained correctly attached",
          "Label appeared after a short delay",
          "Label was applied but took time to display",
          "Label disappeared until the chat was reopened",
          "Label could not be applied or saved",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on business profile setup, automated messages, Quick Replies, catalog management, customer conversations, and labels, rate the overall reliability and user experience of WhatsApp Business from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "Describe one specific WhatsApp Business issue or successful feature you noticed. Mention the exact business feature, such as Business Profile, Greeting/Away Message, Quick Replies, Catalog, or Labels.",
      },
    ],
    reward: 200,
    deadline: "2026-08-29",
    owner: "Meta QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },

  // ============================================================
  // MOBILE APP TESTS
  // ============================================================

  //Facebook Lite Web App
  {
    id: "test-app-8b",
    title: "Facebook Lite Web App",
    category: "app-tests",
    subcategory: "Social Media",
    platform: "mobile",
    appName: "Facebook Lite",
    client: "Meta",
    instructions:
      "Install the official Facebook Lite app on your Android device and sign in to an existing Facebook account. Test the lightweight News Feed, post creation, reactions, comments, notifications, and messaging functionality. IMPORTANT: Ensure Facebook Lite is installed and opened before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Installed the official Facebook Lite app on an Android device",
          "Opened Facebook Lite and waited for the News Feed to finish loading",
          "Signed in successfully to an existing Facebook account",
          "Reached the main Facebook Lite feed",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Launch Facebook Lite from your device's home screen. Did the News Feed open correctly with the navigation controls and existing posts visible?",
        options: [
          "Opened immediately with the feed and controls fully visible",
          "Opened normally after a short delay",
          "Feed content loaded progressively after opening",
          "Several sections remained blank or required reopening",
          "App crashed, froze, or failed to launch",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Scroll through at least five Facebook Lite feed posts. Did text, profile information, post images, and available media load correctly while scrolling?",
        options: [
          "All visible content loaded correctly while scrolling",
          "Most content loaded quickly with a minor delay",
          "Several images or media items loaded slowly",
          "Some posts remained incomplete until refreshing",
          "Feed became unresponsive or failed to load content",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Use the Facebook Lite post composer to create a short text post and publish it. Did the post appear in your feed or profile after publishing?",
        options: [
          "Post published immediately and appeared correctly",
          "Post published after a short delay",
          "Post published but took time to appear",
          "Publishing required retrying",
          "Post could not be published",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Open an existing post and select a reaction such as Like or Love. Did the selected reaction remain visible after returning to the feed?",
        options: [
          "Reaction appeared immediately and remained selected",
          "Reaction appeared after a short delay",
          "Reaction updated slowly after returning to the feed",
          "Reaction changed but reverted temporarily",
          "Reaction could not be registered",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Open the comments section of an existing post and submit a short comment. Did the comment appear under the correct post?",
        options: [
          "Comment appeared immediately under the correct post",
          "Comment appeared after a short delay",
          "Comment required returning to the post before appearing",
          "Comment appeared inconsistently or disappeared after refresh",
          "Comment could not be submitted",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Open the Facebook Lite notification area and select a notification that is available, such as a reaction or comment notification. Did the notification list open and navigate to the related content?",
        options: [
          "Notification opened and correctly navigated to related content",
          "Opened after a short delay",
          "Notification list loaded slowly",
          "Notification appeared but opened the wrong or incomplete content",
          "Notifications could not be opened",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Open the available messaging feature in Facebook Lite and send a short message to an available contact. Did the conversation open and did the message appear as sent?",
        options: [
          "Messaging opened immediately and message was sent",
          "Messaging opened after a short delay",
          "Message sent but delivery status appeared later",
          "Messaging required retrying before sending",
          "Messaging could not be opened or the message could not be sent",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on Facebook Lite launch time, feed loading, post creation, reactions, comments, notifications, and messaging, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "Describe one specific Facebook Lite issue or successful feature you noticed. Mention the exact feature, such as Feed, posting, reactions, comments, notifications, or messaging, and explain what happened.",
      },
    ],
    reward: 200,
    deadline: "2026-08-30",
    owner: "Meta QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },

  // ============================================================
  // WEB APP TESTS
  // ============================================================

  //Notion Web App
  {
    id: "test-web-5",
    title: "Notion Web App",
    category: "web-tests",
    subcategory: "Productivity",
    platform: "web",
    appName: "Notion",
    client: "Notion",
    instructions:
      "Open https://www.notion.so/ in your browser and sign in or create a Notion account. Create a test page, add text and headings, create a database/table, add properties and rows, move blocks, and test page sharing. IMPORTANT: Use a test workspace or test page where possible and do not delete important personal or work content.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Opened https://www.notion.so/ and waited for the website to finish loading",
          "Created a Notion account or logged in successfully",
          "Reached the Notion workspace/home page",
          "Opened an existing test page or created a new test page",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open Notion in your browser and wait for your workspace to load. Did the sidebar, pages, workspace navigation, and main content area appear correctly?",
        options: [
          "Workspace loaded immediately with all main controls visible",
          "Workspace loaded normally after a short delay",
          "Pages or sidebar items appeared progressively",
          "Some workspace sections remained blank until refresh",
          "Notion failed to load the workspace",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Create a new Notion page and give it a specific title such as 'QA Test Page'. Did the page open and save the new title correctly?",
        options: [
          "Page opened and title saved immediately",
          "Title saved after a short delay",
          "Title appeared correctly after leaving the title field",
          "Title required editing or refreshing before it persisted",
          "Page could not be created or title could not be saved",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Add a heading block and a paragraph of text to the test page. Did Notion create the blocks in the correct order and preserve the entered text?",
        options: [
          "Both blocks were created and text was preserved correctly",
          "Blocks appeared after a short delay",
          "One block took longer to render",
          "Block creation required retrying",
          "Blocks could not be created or text was lost",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Create a simple database/table on the page with at least two rows and a property such as Status or Category. Did the database display the rows and property values correctly?",
        options: [
          "Database and rows displayed correctly",
          "Database loaded after a short delay",
          "Rows or property values appeared slowly",
          "Database required refreshing before all rows appeared",
          "Database could not be created or edited",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Edit one database row and change one property value, such as changing Status from 'Not started' to 'Done'. Did the new value save and remain visible after clicking elsewhere?",
        options: [
          "Value changed immediately and remained saved",
          "Value saved after a short delay",
          "Value appeared changed but took time to synchronize",
          "Value reverted until the page was refreshed",
          "Property value could not be changed",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Move one content block above or below another block using Notion's block controls or drag-and-drop. Did the block move to the selected position without changing its content?",
        options: [
          "Block moved exactly where expected and content remained intact",
          "Block moved after a short delay",
          "Movement was slightly difficult but successful",
          "Block moved to an unexpected position",
          "Block could not be moved",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Open the Share menu for the test page and review the available sharing options. If your test setup allows sharing, share the page with another test account or use the available link-sharing option. Did the sharing controls work correctly?",
        options: [
          "Sharing menu opened and the selected sharing option worked",
          "Sharing controls loaded after a short delay",
          "Some sharing options were slow or unclear",
          "Sharing required retrying or refreshing",
          "Sharing controls could not be used",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on workspace loading, page creation, block editing, database management, block movement, and page sharing, rate the overall reliability and user experience of Notion Web from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "Describe one specific Notion issue or successful feature you noticed. Mention the exact area, such as page creation, blocks, databases, properties, drag-and-drop, or sharing, and explain what happened.",
      },
    ],
    reward: 100,
    deadline: "2026-09-03",
    owner: "Notion QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must open website", "Must have account"],
    language: "en",
  },
  // Instagram App
  {
    id: "test-app-39",
    title: "Instagram App",
    category: "app-tests",
    subcategory: "Social Media",
    platform: "mobile",
    appName: "Instagram",
    client: "Meta",
    instructions:
      "Download the Instagram app. Create an account or sign in, then test the home feed, Stories, Reels, search, profile interactions, and direct messaging. IMPORTANT: Ensure you have downloaded and opened the app before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Installed the Instagram app on your device",
          "Opened Instagram and waited for the Home feed to finish loading",
          "Created an account or logged in successfully",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Launch Instagram from your home screen. Did the app open normally and display the Home feed without a crash, blank screen, or persistent loading indicator?",
        options: [
          "Opened immediately and Home feed loaded normally",
          "Opened quickly with a minor loading delay",
          "Took noticeably longer than expected but eventually loaded",
          "Home feed loaded partially or remained stuck",
          "App crashed or failed to open",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Sign in to Instagram using your existing account. Did the login flow complete correctly, including any password, verification, or security-code steps that were presented?",
        options: [
          "Signed in successfully without any issue",
          "Signed in successfully after a minor delay",
          "Required an unexpected extra attempt or retry",
          "Displayed an error or verification problem before completing",
          "Could not sign in",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "On the Instagram Home feed, scroll through at least 5 posts containing a mixture of photos, videos, and Reels. Did the posts, captions, images, and video content load correctly as you scrolled?",
        options: [
          "All content loaded correctly and scrolling was smooth",
          "One item loaded slightly slowly but recovered",
          "Several items had noticeable loading delays",
          "Some posts or media failed to load",
          "The feed became unusable, froze, or crashed",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Open a Story from the Stories row and move through at least 3 Story items. Did the Story images/videos advance correctly and did the Story controls respond when tapped?",
        options: [
          "Stories played and controls worked correctly",
          "One Story had a short loading delay",
          "Several Stories loaded slowly or skipped",
          "A Story failed to load or controls did not respond",
          "Stories could not be opened or the app froze",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Open the Reels tab and watch at least 3 Reels. Did videos start playing, respond to swipe gestures, and maintain normal playback without repeated buffering?",
        options: [
          "All Reels played smoothly and swiping worked normally",
          "One Reel buffered briefly but recovered",
          "Playback had repeated buffering or noticeable delays",
          "One or more Reels failed to play",
          "Reels were unusable or the app became unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Use Instagram Search to find a specific account or public profile, open the profile, and view one of its posts. Did search results and the selected profile load correctly?",
        options: [
          "Search results and profile loaded correctly",
          "Results appeared after a short delay",
          "Search required a retry or results loaded inconsistently",
          "The expected account/profile could not be opened",
          "Search failed or became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Open Instagram Direct Messages, select an existing conversation, and send a short text message. Did the conversation open correctly and did the sent message appear in the chat?",
        options: [
          "Conversation opened and message appeared immediately",
          "Message appeared after a short delay",
          "Message required a retry before appearing",
          "Message could not be sent or did not appear correctly",
          "Direct Messages failed to load or became unresponsive",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on the Home feed, Stories, Reels, Search, and Direct Messages you tested, rate the overall reliability and user experience from 1 to 5.",
      },
      {
        id: "q9",
        type: "text",
        text: "What specific Instagram feature did you test, and what additional issue, unexpected behavior, or improvement would you like to report?",
      },
    ],
    reward: 200,
    deadline: "2026-10-25",
    owner: "Instagram QA",
    status: "open",
    demand: "high",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },

  // Apple Music App
  {
    id: "test-app-40",
    title: "Apple Music App",
    category: "app-tests",
    subcategory: "Entertainment",
    platform: "mobile",
    appName: "Apple Music",
    client: "Apple",
    instructions:
      "Download the Apple Music app. Sign in with an Apple Account if required, then test music search, song playback, queue controls, playlists, downloads, and Library management. IMPORTANT: Ensure you have downloaded and opened the app before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Installed the Apple Music app on your device",
          "Opened Apple Music and waited for the Home or Library screen to finish loading",
          "Signed in with an Apple Account or confirmed that the app is ready to use",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Launch Apple Music from your home screen. Did the app open normally and display the Home, Listen Now, or Library interface without crashing or remaining stuck on a loading screen?",
        options: [
          "Opened immediately and loaded normally",
          "Opened quickly with a minor loading delay",
          "Took noticeably longer but eventually loaded",
          "Loaded only part of the interface or remained stuck",
          "App crashed or failed to open",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Open the account/profile area and verify your Apple Music account is available. If sign-in was required, did the authentication flow complete successfully?",
        options: [
          "Account was recognized and opened correctly",
          "Authentication completed after a short delay",
          "Required a retry before working",
          "Displayed an account or authentication error",
          "Could not access the account",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Use Search to find a specific song or artist, open the result, and start playback. Did the correct song load and begin playing with the expected playback controls?",
        options: [
          "Correct song opened and played immediately",
          "Playback started after a short delay",
          "Search or playback required a retry",
          "Wrong content loaded or playback failed",
          "Search or playback was unavailable",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "While a song is playing, pause it, resume it, skip to the next track, and move the playback position using the progress bar. Did all playback controls respond correctly?",
        options: [
          "All controls responded correctly",
          "One control responded with a slight delay",
          "Several controls were delayed or inconsistent",
          "One or more controls failed",
          "Playback controls were completely unresponsive",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Add a song to an existing playlist or create a new playlist and add the song. Did the playlist save and show the selected song correctly?",
        options: [
          "Song was added and playlist updated immediately",
          "Playlist updated after a short delay",
          "Required a retry before the song appeared",
          "Song could not be added or playlist did not update",
          "Playlist functionality was unavailable",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Open Library and locate the song or playlist you just added. Did the Library reflect the change correctly without requiring repeated refreshes?",
        options: [
          "Library updated immediately",
          "Library updated after a short delay",
          "Required leaving and reopening the Library",
          "New content did not appear correctly",
          "Library failed to load or became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "If your account and subscription allow downloads, download an available song or playlist and check its download status in the Library. Did the download complete correctly?",
        options: [
          "Download completed and content was clearly marked as downloaded",
          "Download completed after a short delay",
          "Download required a retry",
          "Download failed or remained stuck",
          "Download control was unavailable or unresponsive",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on music search, playback controls, playlists, Library updates, and downloads, rate the overall reliability and user experience from 1 to 5.",
      },
      {
        id: "q9",
        type: "text",
        text: "What specific Apple Music feature did you test, and what playback, Library, playlist, download, or usability issue would you like to report?",
      },
    ],
    reward: 200,
    deadline: "2026-10-28",
    owner: "Apple Music QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },

  // X (Twitter) App
  {
    id: "test-app-41",
    title: "X (Twitter) App",
    category: "app-tests",
    subcategory: "Social Media",
    platform: "mobile",
    appName: "X (Twitter)",
    client: "X",
    instructions:
      "Download the X app. Sign in to an account, then test the Home timeline, post creation, replies, likes, reposts, search, media playback, and direct messaging. IMPORTANT: Ensure you have downloaded and opened the app before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Installed the X app on your device",
          "Opened X and waited for the Home timeline to finish loading",
          "Created an account or logged in successfully",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Launch X from your home screen. Did the app open normally and display the Home timeline without a crash, blank screen, or persistent loading state?",
        options: [
          "Opened immediately and timeline loaded normally",
          "Opened quickly with a minor loading delay",
          "Took noticeably longer but eventually loaded",
          "Timeline loaded partially or remained stuck",
          "App crashed or failed to open",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Sign in to your X account. Did the authentication flow complete correctly, including any password, verification, or security checks presented during login?",
        options: [
          "Signed in successfully without any issue",
          "Signed in successfully after a short delay",
          "Required a retry before completing",
          "Displayed an authentication or verification error",
          "Could not sign in",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Scroll through the Home timeline and open several posts containing text, images, and videos. Did the posts and media load correctly as you scrolled?",
        options: [
          "Posts and media loaded correctly with smooth scrolling",
          "One item loaded slightly slowly",
          "Several items had noticeable loading delays",
          "Some posts or media failed to load",
          "Timeline became unusable, froze, or crashed",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Open a post and use the reply field to submit a short reply. Did the reply publish and appear under the correct post?",
        options: [
          "Reply published immediately and appeared correctly",
          "Reply published after a short delay",
          "Required a retry before appearing",
          "Reply failed to publish or appeared incorrectly",
          "Reply control was unavailable or unresponsive",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Like a post and then repost it using the post action controls. Did both actions register and did their selected states update correctly?",
        options: [
          "Like and repost both registered immediately",
          "Both actions worked with a short delay",
          "One action required a retry",
          "One or both actions failed or showed an incorrect state",
          "The action controls were unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Use X Search to find a specific public account or keyword. Did the search results load and allow you to open the expected account or relevant posts?",
        options: [
          "Results loaded correctly and opened as expected",
          "Results appeared after a short delay",
          "Search required a retry",
          "Results were incomplete or the expected content could not be opened",
          "Search failed or became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Open Direct Messages, select an existing conversation, and send a short text message. Did the conversation and message sending work correctly?",
        options: [
          "Conversation opened and message sent successfully",
          "Message sent after a short delay",
          "Required a retry before sending",
          "Message failed to send or did not appear correctly",
          "Direct Messages failed to load or was unresponsive",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on the Home timeline, post interactions, Search, and Direct Messages you tested, rate the overall reliability and user experience from 1 to 5.",
      },
      {
        id: "q9",
        type: "text",
        text: "What specific X feature did you test, and what additional issue, unexpected behavior, or improvement would you like to report?",
      },
    ],
    reward: 200,
    deadline: "2026-11-02",
    owner: "X QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },

  // Coursera App
  {
    id: "test-app-42",
    title: "Coursera App",
    category: "app-tests",
    subcategory: "Education",
    platform: "mobile",
    appName: "Coursera",
    client: "Coursera",
    instructions:
      "Download the Coursera app. Sign in, search for a course, open a course page, test lesson content, video playback, quizzes or assessments where available, and progress tracking. IMPORTANT: Ensure you have downloaded and opened the app before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Installed the Coursera app on your device",
          "Opened Coursera and waited for the Home or course screen to finish loading",
          "Created an account or logged in successfully",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Launch Coursera from your home screen. Did the app open normally and display the Home or learning interface without crashing, a blank screen, or a persistent loading indicator?",
        options: [
          "Opened immediately and loaded normally",
          "Opened quickly with a minor loading delay",
          "Took noticeably longer but eventually loaded",
          "Loaded partially or remained stuck",
          "App crashed or failed to open",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Sign in to your Coursera account. Did the login flow complete correctly and return you to your Coursera learning interface?",
        options: [
          "Signed in successfully without any issue",
          "Signed in after a short delay",
          "Required a retry before completing",
          "Displayed an authentication or account error",
          "Could not sign in",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Use Coursera Search to find a specific course by title or subject, then open its course details page. Did the search results and course information load correctly?",
        options: [
          "Search and course page loaded correctly",
          "Results or course page loaded with a short delay",
          "Required a retry before opening the course",
          "Results were incomplete or course page failed to load",
          "Search or course browsing was unusable",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Open an available course lesson and start the lesson video. Did the video load, play, pause, and resume correctly?",
        options: [
          "Video loaded and all playback controls worked correctly",
          "Video started after a short delay",
          "Playback required a retry or buffered several times",
          "Video failed to play or controls did not work correctly",
          "Lesson video was completely unavailable",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Continue to the next lesson or course activity after watching the current lesson. Did Coursera move to the correct next item and display the expected lesson content?",
        options: [
          "Next lesson opened correctly",
          "Opened after a short delay",
          "Required manually reopening the course",
          "Opened the wrong item or failed to advance",
          "Course navigation was unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "If the course provides a quiz or assessment, open it and answer at least one question. Did the question interface accept your selection and allow you to continue or submit?",
        options: [
          "Answer selection and navigation worked correctly",
          "Worked after a short delay",
          "Required a retry before accepting the answer",
          "Answer selection or submission failed",
          "Quiz or assessment interface was unavailable",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Return to the course or learning dashboard after completing a lesson. Did Coursera correctly show the lesson as completed and update your course progress?",
        options: [
          "Progress updated immediately and correctly",
          "Progress updated after a short delay",
          "Required reopening or refreshing the course",
          "Progress did not reflect the completed lesson correctly",
          "Dashboard or progress tracking failed to load",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on course search, lesson playback, course navigation, assessments, and progress tracking, rate the overall reliability and user experience from 1 to 5.",
      },
      {
        id: "q9",
        type: "text",
        text: "What specific Coursera feature did you test, and what issue with course content, video playback, assessments, navigation, or progress tracking would you like to report?",
      },
    ],
    reward: 200,
    deadline: "2026-11-05",
    owner: "Coursera QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },

  // ============================================================
  // ========================= APP TESTS =========================
  // ============================================================

  // ------------------------------------------------------------
  // Khan Academy App
  // ------------------------------------------------------------
  {
    id: "test-app-43",
    title: "Khan Academy App",
    category: "app-tests",
    subcategory: "Education",
    platform: "mobile",
    appName: "Khan Academy",
    client: "Khan Academy",
    instructions:
      "Download the Khan Academy app. Explore lessons, test exercises, progress tracking, and content recommendations. IMPORTANT: Ensure you have downloaded and opened the app before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Installed the Khan Academy app on your device",
          "Opened the app and waited for full load",
          "Created an account or logged in successfully",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Launch the app from your home screen. How fast did it open, and did you see any crash dialogs, loading failures, or blank screens?",
        options: [
          "Instant, no errors",
          "Fast, minor warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Crashed on launch / failed",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Create an account or sign in. Describe the exact flow and any errors, CAPTCHAs, 2FA prompts, or verification issues you encountered.",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed or error message shown",
          "Could not create or log in at all",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Search for a specific course or topic. Did search results load correctly and navigate to the expected content?",
        options: [
          "Very responsive, no issues",
          "Mostly responsive, minor delay",
          "Noticeable lag or jank",
          "Feature failed to open",
          "App crashed or froze",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Start a lesson and watch the video. Did the video play smoothly and were the playback controls responsive?",
        options: [
          "Completed successfully, no issues",
          "Minor delay or retry needed",
          "Partially worked or incomplete",
          "Failed with clear error",
          "Unknown error / no feedback",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Complete a practice exercise from the lesson. Did the questions load correctly and did the app accept your answers?",
        options: [
          "Instant and reliable",
          "Slight delay but succeeded",
          "Unreliable or inconsistent",
          "Failed with error",
          "Button or feature unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Submit the completed exercise and review your result. Did your score and progress update correctly?",
        options: [
          "Updated instantly",
          "Delayed but appeared",
          "Inconsistent updates",
          "No updates received",
          "App became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Open your learning dashboard and review recommended content. Did your progress and recommended lessons appear correctly?",
        options: [
          "Handled gracefully",
          "Minor glitch but recovered",
          "Poor handling or confusing",
          "Crashed or froze",
          "No error feedback shown",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on all the actions you performed during this test, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "What additional feedback, suggestions, or issues would you like to share about your experience with this app?",
      },
    ],
    reward: 200,
    deadline: "2026-11-08",
    owner: "Khan Academy QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },

  // ------------------------------------------------------------
  // Grammarly App
  // ------------------------------------------------------------
  {
    id: "test-app-44",
    title: "Grammarly App",
    category: "app-tests",
    subcategory: "Productivity",
    platform: "mobile",
    appName: "Grammarly",
    client: "Grammarly",
    instructions:
      "Download the Grammarly app. Test writing suggestions, grammar checking, tone detection, and keyboard integration. IMPORTANT: Ensure you have downloaded and opened the app before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Installed the Grammarly app on your device",
          "Opened the app and waited for full load",
          "Created an account or logged in successfully",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Launch the app from your home screen. How fast did it open, and did you see any crash dialogs, loading failures, or blank screens?",
        options: [
          "Instant, no errors",
          "Fast, minor warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Crashed on launch / failed",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Create an account or sign in. Describe the exact flow and any errors, CAPTCHAs, 2FA prompts, or verification issues you encountered.",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed or error message shown",
          "Could not create or log in at all",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Open the Grammarly editor and paste or type some text. Did the editor load and start checking your writing immediately?",
        options: [
          "Very responsive, no issues",
          "Mostly responsive, minor delay",
          "Noticeable lag or jank",
          "Feature failed to open",
          "App crashed or froze",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Review the grammar and spelling suggestions. Did Grammarly highlight errors correctly and show useful explanations?",
        options: [
          "Completed successfully, no issues",
          "Minor delay or retry needed",
          "Partially worked or incomplete",
          "Failed with clear error",
          "Unknown error / no feedback",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Accept a suggested correction. Did the correction apply correctly without changing unrelated parts of your text?",
        options: [
          "Instant and reliable",
          "Slight delay but succeeded",
          "Unreliable or inconsistent",
          "Failed with error",
          "Button or feature unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Review the overall writing score and readability or tone feedback. Did the metrics update as you edited the text?",
        options: [
          "Updated instantly",
          "Delayed but appeared",
          "Inconsistent updates",
          "No updates received",
          "App became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Enable or open Grammarly's keyboard integration and type text in another supported app. Did the suggestions appear correctly while typing?",
        options: [
          "Handled gracefully",
          "Minor glitch but recovered",
          "Poor handling or confusing",
          "Crashed or froze",
          "No error feedback shown",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on all the actions you performed during this test, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "What additional feedback, suggestions, or issues would you like to share about your experience with this app?",
      },
    ],
    reward: 200,
    deadline: "2026-11-10",
    owner: "Grammarly QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },

  // ------------------------------------------------------------
  // Evernote App
  // ------------------------------------------------------------
  {
    id: "test-app-45",
    title: "Evernote App",
    category: "app-tests",
    subcategory: "Productivity",
    platform: "mobile",
    appName: "Evernote",
    client: "Evernote",
    instructions:
      "Download the Evernote app. Create notes, test organization, search, reminders, and sync features. IMPORTANT: Ensure you have downloaded and opened the app before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Installed the Evernote app on your device",
          "Opened the app and waited for full load",
          "Created an account or logged in successfully",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Launch the app from your home screen. How fast did it open, and did you see any crash dialogs, loading failures, or blank screens?",
        options: [
          "Instant, no errors",
          "Fast, minor warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Crashed on launch / failed",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Create an account or sign in. Describe the exact flow and any errors, CAPTCHAs, 2FA prompts, or verification issues you encountered.",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed or error message shown",
          "Could not create or log in at all",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Open Evernote and create a new note. Did the note editor open and allow you to start typing immediately?",
        options: [
          "Very responsive, no issues",
          "Mostly responsive, minor delay",
          "Noticeable lag or jank",
          "Feature failed to open",
          "App crashed or froze",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Add text, images, and checklists to the note. Did all content types insert correctly and display properly?",
        options: [
          "Completed successfully, no issues",
          "Minor delay or retry needed",
          "Partially worked or incomplete",
          "Failed with clear error",
          "Unknown error / no feedback",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Save the note and use Evernote's search feature to find it. Did the note appear correctly in the search results?",
        options: [
          "Instant and reliable",
          "Slight delay but succeeded",
          "Unreliable or inconsistent",
          "Failed with error",
          "Button or feature unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Organize the note into a notebook and add a relevant tag. Did the organization changes save correctly?",
        options: [
          "Updated instantly",
          "Delayed but appeared",
          "Inconsistent updates",
          "No updates received",
          "App became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Set a reminder on the note and then check the note after syncing. Did the reminder and note data remain correct?",
        options: [
          "Handled gracefully",
          "Minor glitch but recovered",
          "Poor handling or confusing",
          "Crashed or froze",
          "No error feedback shown",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on all the actions you performed during this test, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "What additional feedback, suggestions, or issues would you like to share about your experience with this app?",
      },
    ],
    reward: 200,
    deadline: "2026-11-12",
    owner: "Evernote QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },

  // ============================================================
  // ======================== WEB APP TESTS ======================
  // ============================================================

  // ------------------------------------------------------------
  // X (Twitter) Web App
  // ------------------------------------------------------------
  {
    id: "test-web-7",
    title: "X (Twitter) Web App",
    category: "web-tests",
    subcategory: "Social Media",
    platform: "web",
    appName: "X (Twitter)",
    client: "X",
    instructions:
      "Open your browser and go to www.x.com. Sign in, test the timeline, posts, replies, media uploads, and explore features.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Opened x.com in your browser and waited for full page load",
          "Created an account or logged in successfully",
          "Accessed the main feature being tested",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open the official website in your browser. How fast did the page load, and did you see any loading errors, blank screens, or script failures?",
        options: [
          "Instant, no errors",
          "Fast, minor script warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Failed to load / blank page",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Create an account or sign in. Describe the exact flow you followed and any errors, CAPTCHAs, 2FA prompts, or verification issues you encountered.",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed / error message shown",
          "Could not create or log in at all",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Open your X feed and scroll through posts. Did tweets, images, and videos load correctly as you scrolled?",
        options: [
          "Very responsive, no issues",
          "Mostly responsive, minor delay",
          "Noticeable lag or jank",
          "Feature failed to open",
          "Page or browser crashed",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Compose a new post with text and an image. Did the post creation flow work and did the post publish successfully?",
        options: [
          "Completed successfully, no issues",
          "Minor delay or retry needed",
          "Partially worked or incomplete",
          "Failed with clear error",
          "Unknown error / no feedback",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Like and repost a post. Did both actions register immediately and show the correct counts or status?",
        options: [
          "Instant and reliable",
          "Slight delay but succeeded",
          "Unreliable or inconsistent",
          "Failed with error",
          "Button or feature unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Open a post and reply with a short message. Did the reply publish correctly and appear in the conversation?",
        options: [
          "Updated instantly",
          "Delayed but appeared",
          "Inconsistent updates",
          "No updates received",
          "Page became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "View your profile and check your posts, profile information, and media. Did the profile load correctly and show accurate information?",
        options: [
          "Handled gracefully",
          "Minor glitch but recovered",
          "Poor handling or confusing",
          "Crashed or froze",
          "No error feedback shown",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on all the actions you performed during this test, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "What additional feedback, suggestions, or issues would you like to share about your experience with this web app?",
      },
    ],
    reward: 100,
    deadline: "2026-09-10",
    owner: "X QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must have browser", "Must have account"],
    language: "en",
  },

  // ------------------------------------------------------------
  // LinkedIn Web App
  // ------------------------------------------------------------
  {
    id: "test-web-8",
    title: "LinkedIn Web App",
    category: "web-tests",
    subcategory: "Social Media",
    platform: "web",
    appName: "LinkedIn",
    client: "Microsoft",
    instructions:
      "Open your browser and go to www.linkedin.com. Sign in, test the feed, job search, messaging, and networking features.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Opened https://www.linkedin.com in your browser and waited for full page load",
          "Created an account or logged in successfully",
          "Accessed the main feature being tested",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open the official website in your browser. How fast did the page load, and did you see any loading errors, blank screens, or script failures?",
        options: [
          "Instant, no errors",
          "Fast, minor script warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Failed to load / blank page",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Create an account or sign in. Describe the exact flow you followed and any errors, CAPTCHAs, 2FA prompts, or verification issues you encountered.",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed / error message shown",
          "Could not create or log in at all",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Browse your LinkedIn feed. Did posts, articles, images, and updates load correctly as you scrolled?",
        options: [
          "Very responsive, no issues",
          "Mostly responsive, minor delay",
          "Noticeable lag or jank",
          "Feature failed to open",
          "Page or browser crashed",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Search for a person or company using LinkedIn search. Did search results load correctly and navigate to the expected profile or company page?",
        options: [
          "Completed successfully, no issues",
          "Minor delay or retry needed",
          "Partially worked or incomplete",
          "Failed with clear error",
          "Unknown error / no feedback",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Search for a job matching your interests. Did the job results load correctly and show relevant positions?",
        options: [
          "Instant and reliable",
          "Slight delay but succeeded",
          "Unreliable or inconsistent",
          "Failed with error",
          "Button or feature unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Open a job listing and review its details. Did the job description, company information, and application options load correctly?",
        options: [
          "Updated instantly",
          "Delayed but appeared",
          "Inconsistent updates",
          "No updates received",
          "Page became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Open LinkedIn Messaging and send a message to a connection. Did the conversation open and did the message send successfully?",
        options: [
          "Handled gracefully",
          "Minor glitch but recovered",
          "Poor handling or confusing",
          "Crashed or froze",
          "No error feedback shown",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on all the actions you performed during this test, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "What additional feedback, suggestions, or issues would you like to share about your experience with this web app?",
      },
    ],
    reward: 100,
    deadline: "2026-09-12",
    owner: "LinkedIn QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must have browser", "Must have account"],
    language: "en",
  },

  // ------------------------------------------------------------
  // YouTube Web App
  // ------------------------------------------------------------
  {
    id: "test-web-9",
    title: "YouTube Web App",
    category: "web-tests",
    subcategory: "Entertainment",
    platform: "web",
    appName: "YouTube",
    client: "Google",
    instructions:
      "Open your browser and go to www.youtube.com. Sign in, test video playback, search, recommendations, upload flow, and comments.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Opened https://www.youtube.com and waited for full page load",
          "Created an account or logged in successfully",
          "Accessed the main feature being tested",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open the official website in your browser. How fast did the page load, and did you see any loading errors, blank screens, or script failures?",
        options: [
          "Instant, no errors",
          "Fast, minor script warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Failed to load / blank page",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Create an account or sign in. Describe the exact flow you followed and any errors, CAPTCHAs, 2FA prompts, or verification issues you encountered.",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed / error message shown",
          "Could not create or log in at all",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Use the search bar to find a specific video. Did search results appear correctly and did the selected video load?",
        options: [
          "Very responsive, no issues",
          "Mostly responsive, minor delay",
          "Noticeable lag or jank",
          "Feature failed to open",
          "Page or browser crashed",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "While watching the selected video, open the quality settings and change the playback quality. Did the new quality take effect correctly?",
        options: [
          "Completed successfully, no issues",
          "Minor delay or retry needed",
          "Partially worked or incomplete",
          "Failed with clear error",
          "Unknown error / no feedback",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Open the comments section under the video. Were you able to read existing comments and post a new comment?",
        options: [
          "Instant and reliable",
          "Slight delay but succeeded",
          "Unreliable or inconsistent",
          "Failed with error",
          "Button or feature unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Like the video and add it to a playlist or Watch Later. Did the actions save correctly and remain after refreshing the page?",
        options: [
          "Updated instantly",
          "Delayed but appeared",
          "Inconsistent updates",
          "No updates received",
          "Page became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Apply a filter or sort option to the search results. Did the filter apply correctly and update the results?",
        options: [
          "Handled gracefully",
          "Minor glitch but recovered",
          "Poor handling or confusing",
          "Crashed or froze",
          "No error feedback shown",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on all the actions you performed during this test, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "What additional feedback, suggestions, or issues would you like to share about your experience with this web app?",
      },
    ],
    reward: 100,
    deadline: "2026-09-14",
    owner: "YouTube QA",
    status: "open",
    demand: "high",
    estimatedTime: "3 mins",
    requirements: ["Must have browser", "Must have account"],
    language: "en",
  },

  // ============================================================
  // WEB APPS
  // ============================================================

  // Amazon Web App
  {
    id: "test-web-10",
    title: "Amazon Web App",
    category: "web-tests",
    subcategory: "E-commerce",
    platform: "web",
    appName: "Amazon",
    client: "Amazon Inc",
    instructions:
      "Open your browser and go to www.amazon.com. Browse products, test search, add to cart, checkout flow, and order tracking.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Opened https://www.amazon.com and waited for full page load",
          "Created an account or logged in successfully",
          "Accessed the main feature being tested",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open the official website in your browser. How fast did the page load, and did you see any loading errors, blank screens, or script failures?",
        options: [
          "Instant, no errors",
          "Fast, minor script warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Failed to load / blank page",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Create an account or sign in. Describe the exact flow you followed and any errors, CAPTCHAs, 2FA prompts, or verification issues you encountered.",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed / error message shown",
          "Could not create or log in at all",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Search for a specific product using the search bar. Did search results load correctly and show relevant products?",
        options: [
          "Very responsive, no issues",
          "Mostly responsive, minor delay",
          "Noticeable lag or jank",
          "Feature failed to open",
          "Page or browser crashed",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Open a product page and review the details, images, and price. Did the product page load completely and accurately?",
        options: [
          "Completed successfully, no issues",
          "Minor delay or retry needed",
          "Partially worked or incomplete",
          "Failed with clear error",
          "Unknown error / no feedback",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Add the product to your cart and proceed to checkout. Did the cart update and checkout flow work correctly?",
        options: [
          "Instant and reliable",
          "Slight delay but succeeded",
          "Unreliable or inconsistent",
          "Failed with error",
          "Button or feature unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Share the document with another user by email. Did the sharing dialog open and did the permission settings work?",
        options: [
          "Updated instantly",
          "Delayed but appeared",
          "Inconsistent updates",
          "No updates received",
          "App became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Open a recent document from the home screen. Did it load correctly with all your previous changes intact?",
        options: [
          "Handled gracefully",
          "Minor glitch but recovered",
          "Poor handling or confusing",
          "Crashed or froze",
          "No error feedback shown",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on all the actions you performed during this test, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "What additional feedback, suggestions, or issues would you like to share about your experience with this app?",
      },
    ],
    reward: 100,
    deadline: "2026-09-15",
    owner: "Amazon QA",
    status: "open",
    demand: "high",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },

  // Microsoft 365 Web App
  {
    id: "test-web-11",
    title: "Microsoft 365 Web App",
    category: "web-tests",
    subcategory: "Productivity",
    platform: "web",
    appName: "Microsoft 365",
    client: "Microsoft",
    instructions:
      "Open your browser and go to www.microsoft365.com. Sign in, test Word, Excel, PowerPoint web apps, collaboration, and file sharing.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Opened https://www.microsoft365.com and waited for full page load",
          "Created an account or logged in successfully",
          "Accessed the main feature being tested",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open the official website in your browser. How fast did the page load, and did you see any loading errors, blank screens, or script failures?",
        options: [
          "Instant, no errors",
          "Fast, minor script warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Failed to load / blank page",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Create an account or sign in. Describe the exact flow you followed and any errors, CAPTCHAs, 2FA prompts, or verification issues you encountered.",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed / error message shown",
          "Could not create or log in at all",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Open Microsoft 365 and navigate to a specific app like Word, Excel, or PowerPoint. Did the app launch correctly?",
        options: [
          "Very responsive, no issues",
          "Mostly responsive, minor delay",
          "Noticeable lag or jank",
          "Feature failed to open",
          "Page or browser crashed",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Create a new document or spreadsheet. Did the editor open and allow you to start working immediately?",
        options: [
          "Completed successfully, no issues",
          "Minor delay or retry needed",
          "Partially worked or incomplete",
          "Failed with clear error",
          "Unknown error / no feedback",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Save the document to OneDrive. Did the save complete successfully and did the file appear in your cloud storage?",
        options: [
          "Instant and reliable",
          "Slight delay but succeeded",
          "Unreliable or inconsistent",
          "Failed with error",
          "Button or feature unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Share the document with another user by email. Did the sharing dialog open and did the permission settings work?",
        options: [
          "Updated instantly",
          "Delayed but appeared",
          "Inconsistent updates",
          "No updates received",
          "App became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Switch between Gmail, Drive, and Calendar tabs. Did each app load correctly and preserve your session?",
        options: [
          "Handled gracefully",
          "Minor glitch but recovered",
          "Poor handling or confusing",
          "Crashed or froze",
          "No error feedback shown",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on all the actions you performed during this test, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "What additional feedback, suggestions, or issues would you like to share about your experience with this app?",
      },
    ],
    reward: 100,
    deadline: "2026-09-18",
    owner: "Microsoft 365 QA",
    status: "open",
    demand: "high",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },

  // Google Workspace Web App
  {
    id: "test-web-12",
    title: "Google Workspace Web App",
    category: "web-tests",
    subcategory: "Productivity",
    platform: "web",
    appName: "Google Workspace",
    client: "Google",
    instructions:
      "Open your browser and go to workspace.google.com. Test Gmail, Docs, Drive, Calendar, and Meet integration.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Opened https://workspace.google.com and waited for full page load",
          "Created an account or logged in successfully",
          "Accessed the main feature being tested",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open the official website in your browser. How fast did the page load, and did you see any loading errors, blank screens, or script failures?",
        options: [
          "Instant, no errors",
          "Fast, minor script warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Failed to load / blank page",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Create an account or sign in. Describe the exact flow you followed and any errors, CAPTCHAs, 2FA prompts, or verification issues you encountered.",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed / error message shown",
          "Could not create or log in at all",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Open Gmail and check your inbox. Did emails load correctly and was the interface responsive?",
        options: [
          "Very responsive, no issues",
          "Mostly responsive, minor delay",
          "Noticeable lag or jank",
          "Feature failed to open",
          "Page or browser crashed",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Compose a new email with subject and body. Did the compose window open and allow you to send successfully?",
        options: [
          "Completed successfully, no issues",
          "Minor delay or retry needed",
          "Partially worked or incomplete",
          "Failed with clear error",
          "Unknown error / no feedback",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Open Google Drive and upload a file. Did the upload start and complete correctly?",
        options: [
          "Instant and reliable",
          "Slight delay but succeeded",
          "Unreliable or inconsistent",
          "Failed with error",
          "Button or feature unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Share a Google Doc with another user. Did the sharing dialog open and did permissions apply correctly?",
        options: [
          "Updated instantly",
          "Delayed but appeared",
          "Inconsistent updates",
          "No updates received",
          "App became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Change the site theme from the appearance settings. Did the theme activate correctly and did the site update visually?",
        options: [
          "Handled gracefully",
          "Minor glitch but recovered",
          "Poor handling or confusing",
          "Crashed or froze",
          "No error feedback shown",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on all the actions you performed during this test, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "What additional feedback, suggestions, or issues would you like to share about your experience with this app?",
      },
    ],
    reward: 100,
    deadline: "2026-09-20",
    owner: "Google Workspace QA",
    status: "open",
    demand: "high",
    estimatedTime: "3 mins",
    requirements: ["Must install app", "Must have account"],
    language: "en",
  },
  // ============================================================
  // TEST WEB 13 - WORDPRESS
  // ============================================================

  {
    id: "test-web-13",
    title: "WordPress Web App",
    category: "web-tests",
    subcategory: "Productivity",
    platform: "web",
    appName: "WordPress",
    client: "Automattic",
    instructions:
      "Open your browser and go to wordpress.com. Create or access a free WordPress site, test the dashboard, editor, themes, media, and publishing flow. IMPORTANT: Ensure the website has fully loaded before starting this test.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Opened https://wordpress.com in your browser and waited for the page to fully load",
          "Created an account or logged in successfully",
          "Accessed the WordPress dashboard or main site management area",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open WordPress.com in your browser. How fast did the website load, and did you see any loading errors, blank screens, or broken page elements?",
        options: [
          "Instant, no errors",
          "Fast, minor warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Failed to load / blank page",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Create an account or sign in to WordPress. How smoothly did the authentication process work?",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed or error message shown",
          "Could not create or log in at all",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Open the WordPress dashboard. Did the dashboard load correctly and were the main navigation options easy to access?",
        options: [
          "Very responsive, no issues",
          "Mostly responsive, minor delay",
          "Noticeable lag or jank",
          "Dashboard or feature failed to open",
          "Page or browser crashed",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Create a new blog post containing text and an image. Did the WordPress editor work correctly and allow you to save the post as a draft?",
        options: [
          "Completed successfully, no issues",
          "Minor delay or retry needed",
          "Partially worked or incomplete",
          "Failed with clear error",
          "Unknown error / no feedback",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Preview and publish the blog post. Did the published post appear correctly on the live site with the expected text, image, and formatting?",
        options: [
          "Instant and reliable",
          "Slight delay but succeeded",
          "Unreliable or inconsistent",
          "Failed with error",
          "Publish button or feature was unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Open the site customization or themes area and preview a different theme. Did the theme preview load correctly without affecting your existing site unexpectedly?",
        options: [
          "Updated instantly",
          "Delayed but appeared",
          "Inconsistent or partially updated",
          "Theme preview did not load",
          "Website became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Open the Media Library and upload an image. Did the image upload successfully and become available for use in your site?",
        options: [
          "Handled gracefully",
          "Minor glitch but recovered",
          "Poor handling or confusing",
          "Upload failed or page froze",
          "No useful error feedback was shown",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on all the actions you performed during this test, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "What additional feedback, suggestions, or issues would you like to share about your WordPress experience?",
      },
    ],
    reward: 100,
    deadline: "2026-09-22",
    owner: "WordPress QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must use a web browser", "Must have an account"],
    language: "en",
  },

  // ============================================================
  // TEST WEB 14 - TIKTOK
  // ============================================================

  {
    id: "test-web-14",
    title: "TikTok Web App",
    category: "web-tests",
    subcategory: "Social Media",
    platform: "web",
    appName: "TikTok",
    client: "ByteDance",
    instructions:
      "Open your browser and go to www.tiktok.com. Sign in and test the For You feed, search, video playback, comments, likes, sharing, and creator profiles.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Opened https://www.tiktok.com in your browser and waited for the page to fully load",
          "Created an account or logged in successfully",
          "Accessed the TikTok For You feed",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open TikTok in your browser. How fast did the website load, and did you see any loading errors, blank screens, or broken page elements?",
        options: [
          "Instant, no errors",
          "Fast, minor warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Failed to load / blank page",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Create an account or sign in to TikTok. How smoothly did the authentication process work?",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed / error message shown",
          "Could not create or log in at all",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Browse the TikTok For You feed and scroll through several videos. Did videos load and play smoothly as you moved through the feed?",
        options: [
          "Very responsive, no issues",
          "Mostly responsive, minor delay",
          "Noticeable lag or buffering",
          "Videos failed to load or play",
          "Page or browser crashed",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Use TikTok search to find a specific creator, video, or hashtag. Did the search results load correctly and lead to the expected content?",
        options: [
          "Completed successfully, no issues",
          "Minor delay or retry needed",
          "Partially worked or incomplete",
          "Failed with clear error",
          "Unknown error / no feedback",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Like a video and then add a comment. Did both actions register correctly and did the interface reflect the updated state?",
        options: [
          "Instant and reliable",
          "Slight delay but succeeded",
          "Unreliable or inconsistent",
          "Failed with error",
          "Button or feature was unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Use the Share button on a video. Did the sharing options appear correctly and did the selected share action work?",
        options: [
          "Updated instantly",
          "Delayed but appeared",
          "Inconsistent behavior",
          "Share feature failed",
          "Page became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Open a creator's profile and follow the creator. Did the follow action complete and did the profile reflect the updated follow status?",
        options: [
          "Handled gracefully",
          "Minor glitch but recovered",
          "Poor handling or confusing",
          "Follow action failed or page froze",
          "No useful error feedback was shown",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on all the actions you performed during this test, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "What additional feedback, suggestions, or issues would you like to share about your TikTok web experience?",
      },
    ],
    reward: 100,
    deadline: "2026-09-25",
    owner: "TikTok QA",
    status: "open",
    demand: "high",
    estimatedTime: "3 mins",
    requirements: ["Must use a web browser", "Must have an account"],
    language: "en",
  },

  // ============================================================
  // TEST WEB 15 - NETFLIX
  // ============================================================

  {
    id: "test-web-15",
    title: "Netflix Web App",
    category: "web-tests",
    subcategory: "Entertainment",
    platform: "web",
    appName: "Netflix",
    client: "Netflix Inc",
    instructions:
      "Open your browser and go to www.netflix.com. Sign in and test browsing, video playback, search, My List, profiles, and content recommendations.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Opened https://www.netflix.com in your browser and waited for the page to fully load",
          "Created an account or logged in successfully",
          "Accessed the Netflix homepage and selected a user profile",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open Netflix in your browser. How fast did the website load, and did you see any loading errors, blank screens, or broken interface elements?",
        options: [
          "Instant, no errors",
          "Fast, minor warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Failed to load / blank page",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Sign in to Netflix and select a user profile. Did authentication and profile selection work without unexpected errors?",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed / error message shown",
          "Could not sign in or select a profile",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Browse the Netflix homepage and select a movie or series. Did the title page load correctly and provide the expected information?",
        options: [
          "Very responsive, no issues",
          "Mostly responsive, minor delay",
          "Noticeable lag or jank",
          "Title page failed to open",
          "Page or browser crashed",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Start playing a selected movie or episode. Did playback begin correctly, remain stable, and respond to the basic player controls?",
        options: [
          "Completed successfully, no issues",
          "Minor delay or buffering but worked",
          "Partially worked or frequent buffering",
          "Playback failed with clear error",
          "Unknown error / no feedback",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Pause, resume, and change the playback position using the video controls. Did the player respond correctly to each action?",
        options: [
          "Instant and reliable",
          "Slight delay but succeeded",
          "Unreliable or inconsistent",
          "Player controls failed",
          "Button or player was unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Add a title to My List from its title page. Did the title save successfully and appear in My List?",
        options: [
          "Updated instantly",
          "Delayed but appeared",
          "Inconsistent update",
          "Title was not added",
          "Page became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Use the Netflix search bar to find a specific movie or series. Did search results load correctly and allow you to open the expected title?",
        options: [
          "Handled gracefully",
          "Minor glitch but recovered",
          "Poor handling or confusing",
          "Search failed or page froze",
          "No useful error feedback was shown",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on all the actions you performed during this test, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "What additional feedback, suggestions, or issues would you like to share about your Netflix web experience?",
      },
    ],
    reward: 100,
    deadline: "2026-10-01",
    owner: "Netflix QA",
    status: "open",
    demand: "high",
    estimatedTime: "3 mins",
    requirements: ["Must use a web browser", "Must have an account"],
    language: "en",
  },

  // ============================================================
  // TEST WEB 16 - ZOOM
  // ============================================================

  {
    id: "test-web-16",
    title: "Zoom Web App",
    category: "web-tests",
    subcategory: "Communication",
    platform: "web",
    appName: "Zoom",
    client: "Zoom",
    instructions:
      "Open your browser and go to zoom.us. Sign in or join a test meeting, then test meeting access, microphone, camera, chat, and screen sharing.",
    questions: [
      {
        id: "q0",
        type: "task_check",
        text: "Before starting this test, confirm you have completed the following:",
        checklist: [
          "Opened https://zoom.us in your browser and waited for the page to fully load",
          "Created an account or logged in successfully, or opened a valid test meeting link",
          "Granted browser permissions for microphone and camera if required",
        ],
      },
      {
        id: "q1",
        type: "multiple_choice",
        text: "Open Zoom in your browser. How fast did the website load, and did you see any loading errors, blank screens, or broken interface elements?",
        options: [
          "Instant, no errors",
          "Fast, minor warning",
          "Slow load but functional",
          "Very slow / partial load",
          "Failed to load / blank page",
        ],
      },
      {
        id: "q2",
        type: "multiple_choice",
        text: "Sign in to Zoom or open the provided test meeting. Did the authentication or meeting access flow work correctly?",
        options: [
          "Very smooth, no issues",
          "Minor delays but completed",
          "Confusing steps but worked",
          "Failed / error message shown",
          "Could not sign in or join the meeting",
        ],
      },
      {
        id: "q3",
        type: "multiple_choice",
        text: "Join or start a test Zoom meeting. Did the meeting load correctly and were the microphone and camera permission prompts handled clearly?",
        options: [
          "Very responsive, no issues",
          "Mostly responsive, minor delay",
          "Noticeable delay or confusion",
          "Meeting failed to open",
          "Page or browser crashed",
        ],
      },
      {
        id: "q4",
        type: "multiple_choice",
        text: "Mute and unmute your microphone several times during the meeting. Did Zoom correctly reflect the microphone state each time?",
        options: [
          "Completed successfully, no issues",
          "Minor delay or retry needed",
          "Partially worked or inconsistent",
          "Microphone control failed",
          "Unknown error / no feedback",
        ],
      },
      {
        id: "q5",
        type: "multiple_choice",
        text: "Turn your camera on and off during the meeting. Did the camera preview and meeting interface update correctly?",
        options: [
          "Instant and reliable",
          "Slight delay but succeeded",
          "Unreliable or inconsistent",
          "Camera control failed",
          "Button or camera control was unresponsive",
        ],
      },
      {
        id: "q6",
        type: "multiple_choice",
        text: "Open the in-meeting chat and send a test message. Did the chat panel open and did the message appear successfully?",
        options: [
          "Updated instantly",
          "Delayed but appeared",
          "Inconsistent updates",
          "Message failed to send",
          "Chat became unresponsive",
        ],
      },
      {
        id: "q7",
        type: "multiple_choice",
        text: "Start a screen share during the meeting. Did Zoom request the appropriate browser permission and display the selected screen or window correctly?",
        options: [
          "Handled gracefully",
          "Minor glitch but recovered",
          "Poor handling or confusing",
          "Screen sharing failed or froze",
          "No useful error feedback was shown",
        ],
      },
      {
        id: "q8",
        type: "rating",
        text: "Based on all the actions you performed during this test, rate the overall reliability and user experience from 1 to 5",
      },
      {
        id: "q9",
        type: "text",
        text: "What additional feedback, suggestions, or issues would you like to share about your Zoom web experience?",
      },
    ],
    reward: 100,
    deadline: "2026-10-05",
    owner: "Zoom QA",
    status: "open",
    demand: "medium",
    estimatedTime: "3 mins",
    requirements: ["Must use a web browser", "Must have an account or meeting link"],
    language: "en",
  },

  // ============================================================
  // END OF WEB TEST PROJECTS
  // ============================================================
];

// ============================================================
// GET TEST PROJECTS BY CATEGORY
// ============================================================

export function getTestProjectsByCategory(category?: string): TestProject[] {
  if (!category || category.toLowerCase() === "all") {
    return MOCK_TEST_PROJECTS;
  }

  return MOCK_TEST_PROJECTS.filter(
    (test) => test.category.toLowerCase() === category.toLowerCase(),
  );
}

// ============================================================
// EXPORT MOCK TEST PROJECTS
// ============================================================

export { MOCK_TEST_PROJECTS };
